import React from 'react';
import { Badge, Body } from '@panwds/react-ui';
import { countUniqueCities, countUniqueCitiesAcrossPlanes, countSelectedRegions } from '../utils/regionCounts';

/**
 * Product name plus its region summary (a deduped "N Regions" badge and a per-plane
 * "control plane N · data plane M" subtitle). Used by every `SubscribedProductPanel` in
 * `ManageSubscriptionsModal` so each product card presents a consistent header.
 *
 * By default the badge/subtitle show *available* region counts (how many cities the
 * product could subscribe to). Pass `selectedByPlane` -- an array of
 * `[planeType, selectedRegions]` pairs mirroring `enabledPlanes`, where each
 * `selectedRegions` is that plane's raw `city-*` leaf values -- to instead show *selected*
 * counts, as the revamped ManageSubscriptionsModal product panels do (mock shows
 * "Data Plane 50" in the collapsed header, matching the plane's current selection).
 *
 * Rendered at `Body size="md"` (14px) rather than an unstyled `<span>` -- Tailwind
 * preflight is disabled in this app and no base font-size is set, so an unstyled span
 * inherits the 16px browser default, one size larger than every neighboring PANWDS
 * `Body size="sm"` element and prone to wrapping sooner than necessary.
 *
 * All three pieces sit on one line (per GA design review, matching the mock's collapsed
 * row: "Prisma Access [50 Regions] Control Plane 2 - Data Plane 50"). `flex-wrap` keeps a
 * long product name from overflowing the card at narrow widths -- the plane summary drops
 * to a second line instead of being clipped.
 */
const ProductRegionSummary = ({ productType, enabledPlanes, selectedByPlane, testIdPrefix }) => {
    const productRegionCount = selectedByPlane
        ? countSelectedRegions(selectedByPlane)
        : countUniqueCitiesAcrossPlanes(enabledPlanes);

    const selectedByPlaneMap = selectedByPlane
        ? new Map(selectedByPlane)
        : null;

    return (
        <span className='ssmutw-flex ssmutw-flex-wrap ssmutw-items-center ssmutw-gap-2 ssmutw-min-w-0'>
            <Body size="md" as="span" addClassName="ssmutw-font-semibold">{productType}</Body>
            {productRegionCount > 0 && (
                <Badge
                    appearance="gray"
                    size="xs"
                    dataTestId={`${testIdPrefix}-region-count-badge`}
                    addClassName="ssmutw-whitespace-nowrap ssmutw-shrink-0"
                >
                    {productRegionCount} Regions
                </Badge>
            )}
            {enabledPlanes?.length > 0 && (
                <Body size="sm" as="span" appearance="secondary" addClassName="ssmutw-capitalize">
                    {enabledPlanes
                        .map(([planeType, planeData]) => (selectedByPlaneMap
                            ? `${planeType} plane ${(selectedByPlaneMap.get(planeType) || []).length}`
                            : `${planeType} plane ${countUniqueCities(planeData.locations)}`))
                        .join(' \u00b7 ')}
                </Body>
            )}
        </span>
    );
};

export default React.memo(ProductRegionSummary);
